import { useState } from 'react';
import { IconAlertTriangle, IconArrowUp, IconBuilding, IconCopy, IconDeviceDesktop, IconDeviceIpad, IconDeviceMobile, IconDownload, IconBuildingFactory2, IconTruck, IconLock } from '@tabler/icons-react';
import { PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Drawer, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { DEPTS, ROLES, type Role } from './roles';
import s from './roles.module.css';
import '../i18n';

// Mint-Roles (1536): Hồ sơ vai trò · 22 JD. Danh sách 6 cột phòng + ngăn kéo hồ sơ. Dữ liệu mẫu từ board, chỉ đọc (không đổi quyền thật).

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const DCOL: Record<string, string> = { SALE: 'var(--d-blue)', CSD: 'var(--d-violet)', OPS: 'var(--d-amber)', TRD: 'var(--d-green)', ACC: 'var(--err)', HRD: 'var(--brand)', BOD: 'var(--d-gray)' };
const ZN: [string, string, string][] = [['ezy', 'Ezy', 'var(--brand)'], ['box', 'Box', 'var(--pri)'], ['hei', 'Hei', 'var(--ok)'], ['mee', 'Mee', 'var(--err)'], ['edu', 'Edu', 'var(--d-violet)'], ['mov', 'Mov', 'var(--d-amber)']];
const ZS: Record<string, string> = { main: 'kit.rl.zMain', yes: 'kit.rl.zYes', perm: 'kit.rl.zPerm', no: 'kit.rl.zNo' };
const CTX: Record<string, [string, typeof IconBuilding]> = { vp: ['kit.rl.cVp', IconBuilding], ht: ['kit.rl.cHt', IconBuildingFactory2], xe: ['kit.rl.cXe', IconTruck] };
const DEV: Record<string, [string, typeof IconDeviceDesktop]> = { pc: ['kit.rl.dPc', IconDeviceDesktop], pad: ['kit.rl.dPad', IconDeviceIpad], ph: ['kit.rl.dPh', IconDeviceMobile] };
const NET: Record<string, [string, number]> = { ok: ['kit.rl.nOk', 3], mid: ['kit.rl.nMid', 2], low: ['kit.rl.nLow', 1] };
const FQ: Record<string, string> = { N: 'kit.rl.fN', T: 'kit.rl.fT', Th: 'kit.rl.fTh' };
const deptName = (k: string) => DEPTS.find((d) => d[0] === k)?.[1] ?? k;
type Tr = (k: string, p?: Record<string, string | number>) => string;
const thinTxt = (r: Role, t: Tr) => `${t(r.thinLv === 'thin' ? 'kit.rl.thin' : 'kit.rl.warn')} · ${r.thin}`;
const thinTone = (r: Role) => (r.thinLv === 'thin' ? s.thinErr : s.thinWarn);

function Bars({ lv }: { lv: string }) {
  const t = useT();
  const lab = t('kit.rl.inferNet', { v: t(NET[lv]![0]) });
  return (
    <span className={s.net} role="img" aria-label={lab} title={lab}>
      {[6, 10, 14].map((h, i) => <span key={h} style={{ height: h, background: i < NET[lv]![1] ? (lv === 'low' ? 'var(--err)' : lv === 'mid' ? 'var(--warn)' : 'var(--ok)') : 'var(--line)' }} />)}
    </span>
  );
}

function Card({ r, on, open }: { r: Role; on: boolean; open: () => void }) {
  const t = useT();
  return (
    <button type="button" className={s.card} aria-current={on} aria-label={t('kit.rl.open', { c: r.code, n: r.name })} onClick={open}>
      <span className={s.top}>
        <span className={s.av} aria-hidden="true" title={r.code} style={{ ['--c' as string]: DCOL[r.dept] }}>{r.abbr}</span>
        <span className={s.nm}>
          <b title={r.name}>{r.name}</b>
          <span className={s.meta}><span>{r.code}</span><span title={t('kit.rl.level')}>{r.lv}</span>
            {r.thinLv && <span role="img" title={thinTxt(r, t)} aria-label={thinTxt(r, t)} className={thinTone(r)}><IconAlertTriangle size={14} aria-hidden="true" /></span>}
          </span>
        </span>
      </span>
      <span className={s.sum} title={t('kit.rl.purposeJd')}>{r.sum}</span>
      <span className={s.icons}>
        {r.ctx.map((k) => { const [lk, Ic] = CTX[k]!; const lb = t('kit.rl.inferCtx', { v: t(lk) }); return <span key={k} className={s.ic} role="img" aria-label={lb} title={lb}><Ic size={15} aria-hidden="true" /></span>; })}
        <span className={s.sep} aria-hidden="true" />
        {r.dev.map((k, i) => { const [lk, Ic] = DEV[k]!; const lb = t(i ? 'kit.rl.inferDev2' : 'kit.rl.inferDev1', { v: t(lk) }); return <span key={k} className={`${s.ic} ${i ? '' : s.icOn}`} role="img" aria-label={lb} title={lb}><Ic size={15} aria-hidden="true" /></span>; })}
        <Bars lv={r.net} />
      </span>
      <span className={s.zones}>
        {ZN.filter((z) => r.hub[z[0]] !== 'no').map((z) => { const v = r.hub[z[0]]!; const lb = t('kit.rl.inferZone', { z: z[1], v: t(ZS[v]!) }); return <span key={z[0]} title={lb} aria-label={lb} className={`${s.zn} ${v === 'main' ? s.znMain : ''} ${v === 'perm' ? s.znPerm : ''}`} style={{ ['--z' as string]: z[2] }}>{z[1]}</span>; })}
      </span>
    </button>
  );
}

const Sec = ({ k, n }: { k: string; n?: string }) => <div className={s.sec}><h3>{k}</h3>{n && <span>{n}</span>}</div>;

function Profile({ r }: { r: Role }) {
  const t = useT();
  return (
    <div className={s.dw}>
      <div className={s.dwHead}><h2>{t('kit.rl.fromJd')}</h2><span className={s.pill}>{t('kit.rl.jdBrief')}</span></div>
      {r.thinLv && <div role="note" className={`${s.note} ${thinTone(r)}`}><IconAlertTriangle size={16} aria-hidden="true" /><span>{thinTxt(r, t)}</span></div>}
      <Sec k={t('kit.rl.purpose')} /><p>{r.sum}</p>
      <Sec k={t('kit.rl.duties')} n={t('kit.rl.dutyN', { a: r.duties.length, b: r.nDuty })} /><ol>{r.duties.map((x) => <li key={x}>{x}</li>)}</ol>
      <Sec k={t('kit.rl.authority')} />
      {r.auth.length > 0 && <div className={s.rows}>{r.auth.map(([k, v]) => <div key={k}><b>{k}</b><span>{v}</span></div>)}</div>}
      <div className={s.mute}>{r.authNote}</div>
      <Sec k="KPI" /><div className={s.chips}>{r.kpi.map((x) => <span key={x}>{x}</span>)}</div>
      <Sec k={t('kit.rl.levelDiff')} />
      {r.levels.length > 0
        ? <div className={s.rows}>{r.levels.map(([l, add, res, a]) => <div key={l} title={`${l} · +${add} ${t('kit.rl.tasks')} · ${res} ${t('kit.rl.limits')} · ${a || t('kit.rl.noExtra')}`}><b>{l}</b><span title={t('kit.rl.extraTasks')}>+{add}</span><span title={t('kit.rl.notAllowed')}>−{res}</span><span>{a || t('kit.rl.noExtra')}</span></div>)}</div>
        : <div className={s.mute}>{r.lv.includes('–') ? t('kit.rl.noLevelRows') : t('kit.rl.oneLevel', { l: r.lv })}</div>}
      <div className={s.dwHead}><h2>{t('kit.rl.onSystem')}</h2><span className={s.pill}>{t('kit.rl.infer')}</span></div>
      <div className={s.rows}>
        <div title={r.ctxK}><span>{t('kit.rl.ctx')}</span><b>{r.ctxK}</b></div>
        <div><span>{t('kit.rl.mainDev')}</span><b>{r.dev.map((k) => t(DEV[k]![0])).join(' · ')}</b></div>
        <div><span>{t('kit.rl.conn')}</span><b>{t(NET[r.net]![0])}</b></div>
      </div>
      <Sec k={t('kit.rl.loops')} />
      <div className={s.rows}>{r.loops.map(([k, f, n]) => <div key={k}><b>{k}</b><span title={t('kit.rl.freq')}>{t(FQ[f]!)}</span><span title={t('kit.rl.estPer')}>{n}</span></div>)}</div>
      <Sec k={t('kit.rl.data')} />
      <div className={s.two}>
        <div><span className={s.mute}>{t('kit.rl.mustSee')}</span>{r.see.map((x) => <div key={x}>{x}</div>)}</div>
        <div><span className={s.mute}>{t('kit.rl.mustHide')}</span>{r.hide.map((x) => <div key={x}>{x}</div>)}</div>
      </div>
      <Sec k={t('kit.rl.decide')} />
      <div className={s.rows}>{r.dec.map(([k, v]) => <div key={k}><b>{k}</b><span title={t('kit.rl.threshold')}>{v}</span></div>)}</div>
      <Sec k={t('kit.rl.pains')} />
      <div className={s.rows}>{r.pains.map((x) => <div key={x}><span>{t('kit.rl.assume')}</span><span>{x}</span></div>)}</div>
      <Sec k={t('kit.rl.screens')} /><div className={s.chips}>{r.scr.map(([lab, b]) => <span key={b} title={t('kit.rl.board', { b })}>{lab}</span>)}</div>
      <Sec k={t('kit.rl.hubZones')} />
      <div className={s.chips}>{ZN.map((z) => <span key={z[0]} title={t('kit.rl.inferZone', { z: z[1], v: t(ZS[r.hub[z[0]]!]!) })}>{z[1]} · {t(ZS[r.hub[z[0]]!]!)}</span>)}</div>
      <Sec k={t('kit.rl.impl')} /><ul>{r.impl.map((x) => <li key={x}>{x}</li>)}</ul>
      <div className={s.dwHead}><h2>{t('kit.rl.interviewQ')}</h2><span className={s.pill}>{t('kit.rl.confirmAssume')}</span></div>
      <div className={s.rows}>{r.qs.map((q, i) => <div key={q}><b>{i + 1}</b><span>{q}</span></div>)}</div>
    </div>
  );
}

export function Roles() {
  const t = useT();
  const toast = useToast();
  const forced = readState();
  const state: ScreenState = forced ?? 'ready';
  const [dept, setDept] = useState('all');
  const [thinOn, setThinOn] = useState(false);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const thinN = ROLES.filter((r) => r.thinLv).length;
  const pool = ROLES.filter((r) => (dept === 'all' || r.dept === dept) && (!thinOn || !!r.thinLv));
  const groups = (dept === 'all' ? DEPTS : DEPTS.filter((d) => d[0] === dept)).map((d) => ({ d, cs: pool.filter((r) => r.dept === d[0]) })).filter((g) => g.cs.length);
  const idx = Math.max(0, ROLES.findIndex((r) => r.code === openCode));
  const cur = openCode ? ROLES[idx]! : null;
  const step = (n: number) => setOpenCode(ROLES[(idx + n + ROLES.length) % ROLES.length]!.code);
  const wide = dept === 'all';
  const list = state === 'ready';
  return (
    <>
      <PageHeader
        title={t('kit.rl.title')}
        meta={<><span className={s.chip}>{state === 'noperm' || state === 'empty' ? 0 : ROLES.length} {t('kit.rl.roleUnit')}</span><span className={`${s.chip} ${s.chipWarn}`} title={t('kit.rl.jdVer')}>JD v1.0 · Draft</span></>}
        findHint={false}
        actions={<Button aria-label={t('kit.rl.export')} title={t('kit.rl.export')} onClick={() => toast({ msg: t('kit.rl.exported', { n: ROLES.length * 3 }) })}><IconDownload size={16} aria-hidden="true" /></Button>}
      />
      {list && (
        <div className={s.bar}>
          <div role="tablist" aria-label={t('kit.rl.filterDept')} className={s.seg}>
            {[['all', t('kit.rl.all'), t('kit.rl.allDept')], ...DEPTS.map((d) => [d[0], d[2], d[1]])].map(([k, lab, tip]) => {
              const n = ROLES.filter((r) => (k === 'all' || r.dept === k) && (!thinOn || !!r.thinLv)).length;
              return <button key={k} type="button" role="tab" aria-selected={dept === k} title={tip} onClick={() => setDept(k!)}>{lab}<span>{n}</span></button>;
            })}
          </div>
          <button type="button" className={s.thin} aria-pressed={thinOn} title={t('kit.rl.thinOnly')} onClick={() => setThinOn((v) => !v)}><IconAlertTriangle size={15} aria-hidden="true" />{t('kit.rl.thin')}<span>{thinN}</span></button>
        </div>
      )}
      <StateView state={state === 'empty' ? 'ready' : state} title={state === 'error' ? t('kit.rl.loadErr') : undefined} action={state === 'error' ? <Button onClick={() => location.reload()}>{t('kit.rl.retry')}</Button> : undefined} skeleton={<Skeleton rows={8} />}>
        {state === 'empty' && <div className={s.empty}>{t('kit.rl.emptyMsg')}</div>}
        {state === 'noperm' && <section className={s.lock}><IconLock size={28} aria-hidden="true" /><b>{t('kit.rl.noperm')}</b></section>}
        {list && (
          <div className={`${s.outer} ${wide ? s.all : s.one}`} style={{ ['--n' as string]: groups.length }}>
            {groups.map(({ d, cs }) => (
              <section key={d[0]} aria-label={d[1]} className={s.grp}>
                <div className={s.gh}><span aria-hidden="true" style={{ background: DCOL[d[0]] }} /><h2>{d[1]}</h2><span title={t('kit.rl.roleCount')}>{cs.length}</span></div>
                <div className={s.inner}>
                  {cs.map((r) => <Card key={r.code} r={r} on={r.code === openCode} open={() => setOpenCode(r.code)} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </StateView>
      <Drawer
        open={!!cur && list}
        onOpenChange={(o) => !o && setOpenCode(null)}
        title={cur ? cur.name : ''}
        description={cur ? `${cur.code} · ${deptName(cur.dept)} · ${cur.lv}${cur.rpt ? ` · ${t('kit.rl.reports', { r: cur.rpt })}` : ''}` : undefined}
        footer={cur && (
          <div className={s.foot}>
            <Button onClick={() => toast({ msg: t('kit.rl.copied', { c: cur.code }) })}><IconCopy size={16} aria-hidden="true" />{t('kit.rl.copyQ')}</Button>
            <Button aria-label={t('kit.rl.prev')} title={t('kit.rl.prev')} onClick={() => step(-1)}><IconArrowUp size={16} aria-hidden="true" /></Button>
            <Button aria-label={t('kit.rl.next')} title={t('kit.rl.next')} onClick={() => step(1)}><IconArrowUp size={16} style={{ transform: 'rotate(180deg)' }} aria-hidden="true" /></Button>
            <span className={s.mute}>{idx + 1} / {ROLES.length}</span>
          </div>
        )}
      >
        {cur && <Profile r={cur} />}
      </Drawer>
    </>
  );
}
